import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Zap, Check, X, Loader2, Search, Replace, Type,
  Users, PenTool, ChevronDown, ChevronRight, AlertTriangle,
  ArrowRight, Diff
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../lib/api';

const MODE_CONFIG = {
  span: { label: 'Fix Selection', icon: Type, desc: 'Detect and fix issues in selected text', color: 'text-blue-400' },
  pattern: { label: 'Find & Replace', icon: Replace, desc: 'Replace a term or pattern across scope', color: 'text-violet-400' },
  conflict: { label: 'Resolve Conflict', icon: Users, desc: 'Fix a specific issue from a scan', color: 'text-amber-400' },
  chapter: { label: 'Fix Chapter', icon: PenTool, desc: 'Fix mechanical issues in one chapter', color: 'text-emerald-400' },
};

const QuickFixModal = ({ open, onOpenChange, project, user, onProjectUpdate, selectedText = '', selectedIssue = null }) => {
  const [activeMode, setActiveMode] = useState(selectedText ? 'span' : selectedIssue ? 'conflict' : 'span');
  const [loading, setLoading] = useState(false);

  // Span mode
  const [spanText, setSpanText] = useState(selectedText || '');
  const [spanResult, setSpanResult] = useState(null);

  // Pattern mode
  const [pattern, setPattern] = useState('');
  const [replacement, setReplacement] = useState('');
  const [patternScope, setPatternScope] = useState('manuscript');
  const [patternChapter, setPatternChapter] = useState(1);

  // Chapter mechanical mode
  const [chapterNum, setChapterNum] = useState(1);
  const [chapterResult, setChapterResult] = useState(null);
  const [selectedFixes, setSelectedFixes] = useState(new Set());

  // Conflict mode
  const [conflictIssueId, setConflictIssueId] = useState(selectedIssue?.issue_id || '');
  const [conflictFix, setConflictFix] = useState('');

  // ========== SPAN FIX ==========
  const analyzeSpan = useCallback(async () => {
    if (!spanText.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quick-fix/span`, {
        project_id: project.id,
        text: spanText,
      });
      setSpanResult(res.data);
      if (!res.data.has_changes) toast.info('No issues found in selection');
    } catch (e) { toast.error('Analysis failed'); }
    setLoading(false);
  }, [spanText, project]);

  const applySpanFixes = async (fixes) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quick-fix/apply`, {
        project_id: project.id,
        fixes: fixes.map(f => ({ original: f.original, replacement: f.replacement, type: f.type })),
      });
      toast.success(res.data.message);
      if (res.data.applied > 0 && onProjectUpdate) await onProjectUpdate();
      setSpanResult(null);
      setSpanText('');
    } catch (e) { toast.error('Failed to apply'); }
    setLoading(false);
  };

  // ========== PATTERN FIX ==========
  const applyPattern = async () => {
    if (!pattern.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quick-fix/pattern`, {
        project_id: project.id,
        pattern,
        replacement,
        scope: patternScope,
        chapter_number: patternScope === 'chapter' ? patternChapter : 0,
      });
      toast.success(`${res.data.replacements} replacement${res.data.replacements !== 1 ? 's' : ''} made (${res.data.scope})`);
      if (res.data.replacements > 0 && onProjectUpdate) onProjectUpdate();
      setPattern('');
      setReplacement('');
    } catch (e) { toast.error('Replace failed'); }
    setLoading(false);
  };

  // ========== CHAPTER MECHANICAL ==========
  const analyzeChapter = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quick-fix/chapter-mechanical`, {
        project_id: project.id,
        chapter_number: chapterNum,
      });
      setChapterResult(res.data);
      setSelectedFixes(new Set(res.data.fixes?.filter(f => f.classification === 'safe_auto_fix').map((_, i) => i) || []));
      if (!res.data.has_changes) toast.info('No mechanical issues found');
    } catch (e) { toast.error('Analysis failed'); }
    setLoading(false);
  };

  const applyChapterFixes = async () => {
    if (!chapterResult) return;
    const fixes = chapterResult.fixes.filter((_, i) => selectedFixes.has(i));
    if (!fixes.length) return;

    setLoading(true);
    try {
      const res = await axios.post(`${API}/quick-fix/apply`, {
        project_id: project.id,
        fixes: fixes.map(f => ({ original: f.original, replacement: f.replacement, type: f.type })),
      });
      toast.success(res.data.message);
      // Force content refresh so editor shows updated text
      if (res.data.applied > 0 && onProjectUpdate) {
        await onProjectUpdate();
      }
      setChapterResult(null);
      setSelectedFixes(new Set());
    } catch (e) { toast.error('Failed'); }
    setLoading(false);
  };

  // ========== CONFLICT ==========
  const resolveConflict = async () => {
    if (!conflictIssueId) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quick-fix/conflict`, {
        project_id: project.id,
        issue_id: conflictIssueId,
        chosen_fix: conflictFix,
      });
      if (res.data.success) {
        toast.success(res.data.message);
        if (onProjectUpdate) onProjectUpdate();
        setConflictIssueId('');
        setConflictFix('');
      } else {
        toast.error(res.data.message);
      }
    } catch (e) { toast.error('Failed to resolve'); }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl bg-slate-900 border-slate-700 text-white" data-testid="quick-fix-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Zap className="w-5 h-5 text-violet-400" />
            Quick Fix (Basic)
            <Badge variant="outline" className="text-violet-400 border-violet-500/50 text-xs ml-2">LOCAL</Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Mode tabs */}
        <div className="flex gap-1 border-b border-slate-700 pb-0.5">
          {Object.entries(MODE_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (
              <button key={key} onClick={() => { setActiveMode(key); setSpanResult(null); setChapterResult(null); }}
                data-testid={`qf-mode-${key}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-t transition-all ${activeMode === key ? 'bg-slate-800 text-white border-b-2 border-violet-400' : 'text-slate-500 hover:text-slate-300'}`}>
                <Icon className="w-3 h-3" />
                {cfg.label}
              </button>
            );
          })}
        </div>

        {/* ========== SPAN MODE ========== */}
        {activeMode === 'span' && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">{MODE_CONFIG.span.desc}</p>
            <textarea value={spanText} onChange={(e) => setSpanText(e.target.value)}
              placeholder="Paste or type the text you want to fix..."
              className="w-full h-24 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 px-3 py-2 text-xs resize-none focus:border-violet-500 focus:outline-none"
              data-testid="qf-span-input" />
            <div className="flex gap-2">
              <Button onClick={analyzeSpan} disabled={loading || !spanText.trim()} className="bg-violet-600 hover:bg-violet-500 text-xs" data-testid="qf-analyze-span">
                {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
                Analyze
              </Button>
            </div>

            {spanResult && (
              <div className="space-y-2">
                {spanResult.fixes.length === 0 ? (
                  <p className="text-xs text-emerald-400">No issues detected in this text.</p>
                ) : (
                  <>
                    <p className="text-xs text-slate-300 font-medium">{spanResult.fix_count} fix{spanResult.fix_count !== 1 ? 'es' : ''} found:</p>
                    {spanResult.fixes.map((fix, i) => (
                      <FixPreviewCard key={i} fix={fix} onApply={() => applySpanFixes([fix])} />
                    ))}
                    {spanResult.fixes.length > 1 && (
                      <Button onClick={() => applySpanFixes(spanResult.fixes)} disabled={loading}
                        className="bg-emerald-600 hover:bg-emerald-500 text-xs w-full" data-testid="qf-apply-all-span">
                        <Check className="w-3 h-3 mr-1" /> Apply All {spanResult.fixes.length} Fixes
                      </Button>
                    )}
                  </>
                )}

                {/* Diff preview */}
                {spanResult.has_changes && (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-3">
                      <p className="text-[10px] text-slate-500 mb-1 flex items-center gap-1"><Diff className="w-3 h-3" /> Preview</p>
                      <div className="text-xs space-y-1">
                        <p className="text-red-300/70 line-through">{spanResult.original_text}</p>
                        <ArrowRight className="w-3 h-3 text-slate-500" />
                        <p className="text-emerald-300">{spanResult.preview_text}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

        {/* ========== PATTERN MODE ========== */}
        {activeMode === 'pattern' && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">{MODE_CONFIG.pattern.desc}</p>
            <div className="space-y-2">
              <div>
                <label className="text-[10px] text-slate-500 mb-1 block">Find</label>
                <input value={pattern} onChange={(e) => setPattern(e.target.value)}
                  placeholder='e.g. "Angel Fall Coffee" or "Cassy"'
                  className="w-full rounded bg-slate-800 border border-slate-700 text-slate-200 px-3 py-1.5 text-xs focus:border-violet-500 focus:outline-none"
                  data-testid="qf-pattern-find" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 mb-1 block">Replace with</label>
                <input value={replacement} onChange={(e) => setReplacement(e.target.value)}
                  placeholder='e.g. "Angel Falls Coffee" or "Cassie"'
                  className="w-full rounded bg-slate-800 border border-slate-700 text-slate-200 px-3 py-1.5 text-xs focus:border-violet-500 focus:outline-none"
                  data-testid="qf-pattern-replace" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[10px] text-slate-500">Scope:</label>
                <div className="flex gap-1">
                  <button onClick={() => setPatternScope('manuscript')} data-testid="qf-scope-manuscript"
                    className={`px-2 py-1 rounded text-[10px] font-medium ${patternScope === 'manuscript' ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-400'}`}>
                    Entire Manuscript
                  </button>
                  <button onClick={() => setPatternScope('chapter')} data-testid="qf-scope-chapter"
                    className={`px-2 py-1 rounded text-[10px] font-medium ${patternScope === 'chapter' ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-400'}`}>
                    Chapter Only
                  </button>
                </div>
                {patternScope === 'chapter' && (
                  <select value={patternChapter} onChange={(e) => setPatternChapter(parseInt(e.target.value))}
                    className="rounded bg-slate-800 border border-slate-700 text-slate-200 px-2 py-1 text-xs">
                    {(project?.chapter_outline || []).map(ch => (
                      <option key={ch.number} value={ch.number}>Ch {ch.number}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <Button onClick={applyPattern} disabled={loading || !pattern.trim()}
              className="bg-violet-600 hover:bg-violet-500 text-xs" data-testid="qf-apply-pattern">
              {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Replace className="w-3 h-3 mr-1" />}
              Replace All
            </Button>
          </div>
        )}

        {/* ========== CONFLICT MODE ========== */}
        {activeMode === 'conflict' && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">{MODE_CONFIG.conflict.desc}</p>
            {selectedIssue ? (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-3 space-y-2">
                  <p className="text-xs text-white">{selectedIssue.message}</p>
                  {selectedIssue.source_span && (
                    <code className="text-xs text-red-300 bg-red-500/10 px-2 py-1 rounded block">{selectedIssue.source_span}</code>
                  )}
                  {selectedIssue.suggested_fix && (
                    <code className="text-xs text-emerald-300 bg-emerald-500/10 px-2 py-1 rounded block">{selectedIssue.suggested_fix}</code>
                  )}
                  {selectedIssue.alternate_options?.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {selectedIssue.alternate_options.map((alt, i) => (
                        <Badge key={i} className="text-[10px] bg-slate-700 text-slate-300 cursor-pointer hover:bg-slate-600"
                          onClick={() => setConflictFix(alt)}>{alt}</Badge>
                      ))}
                    </div>
                  )}
                  <input value={conflictFix} onChange={(e) => setConflictFix(e.target.value)}
                    placeholder="Custom fix (or leave empty for suggested)"
                    className="w-full rounded bg-slate-800 border border-slate-700 text-slate-200 px-2 py-1.5 text-xs" />
                  <Button onClick={resolveConflict} disabled={loading}
                    className="bg-amber-600 hover:bg-amber-500 text-xs">
                    {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                    Resolve
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div>
                <label className="text-[10px] text-slate-500 mb-1 block">Issue ID (from Find & Fix scan)</label>
                <div className="flex gap-2">
                  <input value={conflictIssueId} onChange={(e) => setConflictIssueId(e.target.value)}
                    placeholder="Paste issue ID"
                    className="flex-1 rounded bg-slate-800 border border-slate-700 text-slate-200 px-2 py-1.5 text-xs" />
                  <Button onClick={resolveConflict} disabled={loading || !conflictIssueId}
                    className="bg-amber-600 hover:bg-amber-500 text-xs">
                    <Check className="w-3 h-3 mr-1" /> Resolve
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========== CHAPTER MECHANICAL MODE ========== */}
        {activeMode === 'chapter' && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">{MODE_CONFIG.chapter.desc}</p>
            <div className="flex gap-2 items-center">
              <select value={chapterNum} onChange={(e) => setChapterNum(parseInt(e.target.value))}
                className="rounded bg-slate-800 border border-slate-700 text-slate-200 px-3 py-1.5 text-xs"
                data-testid="qf-chapter-select">
                {(project?.chapter_outline || []).map(ch => (
                  <option key={ch.number} value={ch.number}>Chapter {ch.number}: {ch.title || `Chapter ${ch.number}`}</option>
                ))}
              </select>
              <Button onClick={analyzeChapter} disabled={loading} className="bg-emerald-600 hover:bg-emerald-500 text-xs" data-testid="qf-analyze-chapter">
                {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
                Scan Chapter
              </Button>
            </div>

            {chapterResult && (
              <div className="space-y-2">
                {chapterResult.fixes.length === 0 ? (
                  <p className="text-xs text-emerald-400">No mechanical issues found in Chapter {chapterNum}.</p>
                ) : (
                  <>
                    <p className="text-xs text-slate-300 font-medium">{chapterResult.fixes.length} issue{chapterResult.fixes.length !== 1 ? 's' : ''} found:</p>
                    <div className="max-h-[250px] overflow-y-auto space-y-1 pr-1">
                      {chapterResult.fixes.map((fix, i) => (
                        <div key={i} className="flex items-start gap-2 bg-slate-800/70 border border-slate-700/50 rounded-lg p-2.5 text-xs" data-testid={`qf-fix-row-${i}`}>
                          <input type="checkbox" checked={selectedFixes.has(i)}
                            onChange={() => setSelectedFixes(prev => {
                              const next = new Set(prev);
                              next.has(i) ? next.delete(i) : next.add(i);
                              return next;
                            })}
                            className="rounded border-slate-600 mt-0.5 accent-emerald-500 w-3.5 h-3.5 flex-shrink-0" />
                          <Badge className={`text-[10px] border-0 flex-shrink-0 ${fix.type === 'spelling' ? 'bg-blue-500/20 text-blue-300' : fix.type === 'punctuation' ? 'bg-cyan-500/20 text-cyan-300' : fix.type === 'repeated_word' ? 'bg-amber-500/20 text-amber-300' : 'bg-pink-500/20 text-pink-300'}`}>
                            {fix.type.replace('_', ' ')}
                          </Badge>
                          <div className="flex flex-wrap items-center gap-1 min-w-0 flex-1">
                            <code className="text-red-300 bg-red-500/15 px-1.5 py-0.5 rounded text-[11px] break-all max-w-[200px] truncate" title={fix.original}>{fix.original}</code>
                            <ArrowRight className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                            <code className="text-emerald-300 bg-emerald-500/15 px-1.5 py-0.5 rounded text-[11px] break-all max-w-[200px] truncate" title={fix.replacement}>{fix.replacement}</code>
                          </div>
                          <span className="text-slate-500 text-[10px] flex-shrink-0">{(fix.confidence * 100).toFixed(0)}%</span>
                        </div>
                      ))}
                    </div>
                    <Button onClick={applyChapterFixes} disabled={loading || selectedFixes.size === 0}
                      className="bg-emerald-600 hover:bg-emerald-500 text-xs w-full" data-testid="qf-apply-chapter">
                      <Check className="w-3 h-3 mr-1" /> Apply {selectedFixes.size} Selected Fix{selectedFixes.size !== 1 ? 'es' : ''}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const FixPreviewCard = ({ fix, onApply }) => (
  <div className="flex items-start gap-2 bg-slate-800/70 border border-slate-700/50 rounded-lg p-2.5 text-xs">
    <Badge className={`text-[10px] border-0 flex-shrink-0 ${fix.type === 'repeated_word' ? 'bg-amber-500/20 text-amber-300' : fix.type === 'spelling' ? 'bg-blue-500/20 text-blue-300' : fix.type === 'punctuation' ? 'bg-cyan-500/20 text-cyan-300' : fix.type === 'character' ? 'bg-violet-500/20 text-violet-300' : 'bg-pink-500/20 text-pink-300'}`}>
      {fix.type.replace('_', ' ')}
    </Badge>
    <div className="flex flex-wrap items-center gap-1 min-w-0 flex-1">
      <code className="text-red-300 bg-red-500/15 px-1.5 py-0.5 rounded text-[11px] break-all max-w-[200px] truncate" title={fix.original}>{fix.original}</code>
      <ArrowRight className="w-3 h-3 text-emerald-400 flex-shrink-0" />
      <code className="text-emerald-300 bg-emerald-500/15 px-1.5 py-0.5 rounded text-[11px] break-all max-w-[200px] truncate" title={fix.replacement}>{fix.replacement}</code>
    </div>
    <span className="text-slate-500 text-[10px] flex-shrink-0">{(fix.confidence * 100).toFixed(0)}%</span>
    <Button size="sm" onClick={onApply} className="bg-emerald-600 hover:bg-emerald-500 text-[10px] h-6 px-2 flex-shrink-0">
      Apply
    </Button>
  </div>
);

export default QuickFixModal;
